import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Spinner,
  Banner,
  DescriptionList,
  DataTable,
  Divider,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';

interface Member {
  id: number;
  email: string;
  name: string;
  picture: string | null;
  role: string;
  joined_at: string;
}

interface Shop {
  id: string;
  domain: string;
  name: string;
  installed_at: string;
  uninstalled_at: string | null;
}

interface Customer {
  id: string;
  name: string;
  slug: string;
  stripe_subscription_status: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  stripe_current_period_end: string | null;
  billing_company_name: string | null;
  billing_vat_number: string | null;
  billing_vat_verified: number;
  billing_contact_name: string | null;
  billing_phone: string | null;
  billing_address_line1: string | null;
  billing_address_line2: string | null;
  billing_city: string | null;
  billing_postal_code: string | null;
  billing_country: string | null;
  created_at: string;
  updated_at: string;
}

export default function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { fetchApi } = useApi();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [shops, setShops] = useState<Shop[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadCustomer() {
      if (!id) return;

      const response = await fetchApi<{
        success: boolean;
        customer?: Customer;
        members?: Member[];
        shops?: Shop[];
        error?: string;
      }>(`/super-admin/customers/${id}`);

      if (response?.success && response.customer) {
        setCustomer(response.customer);
        setMembers(response.members || []);
        setShops(response.shops || []);
      } else {
        setError(response?.error || 'Failed to load customer');
      }

      setLoading(false);
    }

    loadCustomer();
  }, [id, fetchApi]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case 'active':
        return <Badge tone="success">Active</Badge>;
      case 'trialing':
        return <Badge tone="info">Trial</Badge>;
      case 'canceled':
        return <Badge tone="critical">Canceled</Badge>;
      case 'unpaid':
        return <Badge tone="warning">Unpaid</Badge>;
      case 'past_due':
        return <Badge tone="warning">Past Due</Badge>;
      default:
        return <Badge tone="attention">No Subscription</Badge>;
    }
  };

  if (loading) {
    return (
      <Page>
        <Layout>
          <Layout.Section>
            <InlineStack align="center">
              <Spinner size="large" />
            </InlineStack>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (error || !customer) {
    return (
      <Page
        title="Customer Not Found"
        backAction={{ content: 'Back', onAction: () => navigate('/admin/customers') }}
      >
        <Layout>
          <Layout.Section>
            <Banner tone="critical">{error || 'Customer not found'}</Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const memberRows = members.map((member) => [
    <InlineStack key={member.id} gap="200" blockAlign="center">
      {member.picture ? (
        <img
          src={member.picture}
          alt={member.name}
          style={{ width: 32, height: 32, borderRadius: '50%' }}
        />
      ) : (
        <div style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          backgroundColor: '#e4e5e7',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 14,
          fontWeight: 500,
        }}>
          {member.name?.charAt(0) || 'U'}
        </div>
      )}
      <BlockStack gap="0">
        <Text as="span" fontWeight="medium">{member.name}</Text>
        <Text as="span" tone="subdued" variant="bodySm">{member.email}</Text>
      </BlockStack>
    </InlineStack>,
    <Badge key={`role-${member.id}`} tone={member.role === 'owner' ? 'success' : 'info'}>
      {member.role}
    </Badge>,
    formatDate(member.joined_at),
  ]);

  const shopRows = shops.map((shop) => [
    <BlockStack key={shop.id} gap="0">
      <Text as="span" fontWeight="medium">{shop.name || shop.domain}</Text>
      <Text as="span" tone="subdued" variant="bodySm">{shop.domain}</Text>
    </BlockStack>,
    shop.uninstalled_at ? (
      <Badge key={`status-${shop.id}`} tone="critical">Disconnected</Badge>
    ) : (
      <Badge key={`status-${shop.id}`} tone="success">Connected</Badge>
    ),
    formatDate(shop.installed_at),
  ]);

  return (
    <Page
      title={customer.name}
      subtitle={`${customer.slug}.whenlyapp.com`}
      backAction={{ content: 'Customers', onAction: () => navigate('/admin/customers') }}
      titleMetadata={getStatusBadge(customer.stripe_subscription_status)}
    >
      <Layout>
        {/* Overview */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Organization Details</Text>
              <DescriptionList
                items={[
                  { term: 'Organization Name', description: customer.name },
                  { term: 'Subdomain', description: `${customer.slug}.whenlyapp.com` },
                  { term: 'Created', description: formatDate(customer.created_at) },
                  { term: 'Subscription Status', description: customer.stripe_subscription_status || 'None' },
                  { term: 'Current Period Ends', description: formatDate(customer.stripe_current_period_end) },
                  { term: 'Stripe Customer ID', description: customer.stripe_customer_id || '-' },
                ]}
              />
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Billing Details */}
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Billing Details</Text>
              <DescriptionList
                items={[
                  { term: 'Company Name', description: customer.billing_company_name || '-' },
                  { term: 'Contact Name', description: customer.billing_contact_name || '-' },
                  { term: 'Phone', description: customer.billing_phone || '-' },
                  {
                    term: 'VAT Number',
                    description: customer.billing_vat_number ? (
                      <InlineStack gap="200">
                        <Text as="span">{customer.billing_vat_number}</Text>
                        {customer.billing_vat_verified ? (
                          <Badge tone="success">Verified</Badge>
                        ) : (
                          <Badge tone="attention">Unverified</Badge>
                        )}
                      </InlineStack>
                    ) : '-'
                  },
                ]}
              />
              <Divider />
              <Text as="h3" variant="headingSm">Address</Text>
              <Text as="p">
                {customer.billing_address_line1 || '-'}
                {customer.billing_address_line2 && <><br />{customer.billing_address_line2}</>}
                {(customer.billing_city || customer.billing_postal_code) && (
                  <><br />{customer.billing_city} {customer.billing_postal_code}</>
                )}
                {customer.billing_country && <><br />{customer.billing_country}</>}
              </Text>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Stats */}
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Usage</Text>
              <InlineStack gap="800">
                <BlockStack gap="100">
                  <Text as="p" variant="headingXl">{members.length}</Text>
                  <Text as="p" tone="subdued">Team Members</Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text as="p" variant="headingXl">{shops.length}</Text>
                  <Text as="p" tone="subdued">Connected Shops</Text>
                </BlockStack>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Team Members */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Team Members ({members.length})</Text>
              {members.length === 0 ? (
                <Text as="p" tone="subdued">No team members</Text>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'text', 'text']}
                  headings={['Member', 'Role', 'Joined']}
                  rows={memberRows}
                />
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Connected Shops */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Connected Shopify Stores ({shops.length})</Text>
              {shops.length === 0 ? (
                <Text as="p" tone="subdued">No connected stores</Text>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'text', 'text']}
                  headings={['Store', 'Status', 'Connected']}
                  rows={shopRows}
                />
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
